import { t } from "@travily/i18n";
import type { RankingExplanation, RankingFactorScore } from "@travily/types/pending/models/DestinationSearchHit";
import { CardTag } from "@ui/components/Card";
import { PopoverSurface } from "@ui/components/PopoverSurface";
import { Button } from "@ui/primitives/Button";
import { DialogTrigger } from "@ui/primitives/Popover";
import { factorLabel } from "@/lib/ranking-labels";

// Four is what fits before the list stops being an answer and starts being the
// disclosure panel again — and past the fourth, contributions are close enough
// that their order is noise.
const SHOWN_FACTORS = 4;
// Below this a factor's placing rests on a fraction of its evidence, which the
// reader has to be told before they weigh it.
const THIN_EVIDENCE = 0.75;

function percent(fraction: number): number {
  return Math.round(fraction * 100);
}

function FactorRow({ score }: { score: RankingFactorScore }) {
  return (
    <li className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-neutral-800 text-sm">{factorLabel(score.key)}</span>
        <span className="text-neutral-500 text-xs tabular-nums">
          {t("components.views.destinations.rank_explanation.beats", { percent: percent(score.percentile) })}
        </span>
      </div>
      {/* The bar is the same number as the text beside it, for readers who scan
          rather than read. aria-hidden so it is not announced twice. */}
      <div aria-hidden="true" className="h-1 w-full overflow-hidden rounded-full bg-neutral-100">
        <div className="h-full rounded-full bg-purple-400" style={{ width: `${percent(score.percentile)}%` }} />
      </div>
      {score.confidence < THIN_EVIDENCE ? (
        <span className="text-neutral-500 text-xs">{t("components.views.destinations.rank_explanation.thin")}</span>
      ) : null}
    </li>
  );
}

export interface RankExplanationProps {
  explanation: RankingExplanation;
  rank: number;
}

/** The rank badge, made to answer for itself.
 *
 *  The card is a single link, so the trigger has to escape the link's press
 *  target the way `CardFavoriteButton` does in the opposite corner. */
export function RankExplanation({ explanation, rank }: RankExplanationProps) {
  const shown = explanation.factors.slice(0, SHOWN_FACTORS);

  return (
    <DialogTrigger>
      <Button
        aria-label={t("components.views.destinations.rank_explanation.trigger_label", { rank })}
        className="cursor-pointer rounded-full outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
      >
        <CardTag shape="circle">{rank}</CardTag>
      </Button>
      <PopoverSurface className="max-w-[22rem] p-4" placement="bottom start">
        <div className="flex flex-col gap-3">
          <h2 className="font-semibold text-neutral-900 text-sm">
            {t("components.views.destinations.rank_explanation.title", { rank })}
          </h2>
          <ul className="flex flex-col gap-3">
            {shown.map((score) => (
              <FactorRow key={score.key} score={score} />
            ))}
          </ul>
          {explanation.missing.length > 0 ? (
            <p className="border-neutral-100 border-t pt-3 text-neutral-500 text-xs">
              {t("components.views.destinations.rank_explanation.missing", {
                factors: explanation.missing.map(factorLabel).join(", "),
              })}
            </p>
          ) : null}
        </div>
      </PopoverSurface>
    </DialogTrigger>
  );
}
